import {
  capacityVariantFor,
  computeCapacity,
  DEFAULT_CURRENCY,
  missingRequiredFieldIds,
  resolveFieldLabel,
  resolveFixedBuffer,
  withoutWrongFrequencyCapacityKeys,
} from "@repo/shared";
import type {
  FinancialEntryItem,
  FinancialFieldDefinition,
  FinancialSituationAccountEntry,
  FinancialSituationInput,
  FinancialSituationModelSettings,
  FinancialSituationStatusDef,
  FsClientType,
  LiquidityBufferMode,
  MissingRequirementReason,
} from "@repo/shared/types";
import { LIQUIDITY_BUFFER_MODES } from "@repo/shared/types";
import { TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType, IncomeStabilityType } from "@/lib/types/flow";
import { cn } from "@/lib/utils";
import { InfoNote } from "../info-note";
import { AssetsTab } from "./assets-tab";
import { type AnnualField, CapacityTab, type TriangleField } from "./capacity-tab";
import { CommitmentsTab } from "./commitments-tab";
import { T } from "./financial-situation-widget.i18n";
import { IncomeTab } from "./income-tab";
import { LiabilitiesTab } from "./liabilities-tab";
import { SummaryTab } from "./summary-tab";
import { getFieldLabel } from "./translation-utils";
import { MAX_ITEMS_PER_CATEGORY } from "./utils";

/**
 * QP-176 — "Fill from ENIN" targets the seeded company default fields by id;
 * the button is hidden when the flow's model has none of them.
 */
const ENIN_FILL_TARGETS = [
  { fieldId: "company_operating_revenue", key: "revenue", category: "income" },
  { fieldId: "company_net_result", key: "netIncome", category: "income" },
  { fieldId: "company_cash", key: "cashAndDeposits", category: "asset" },
] as const;

export type EninPrefill = {
  year: number | null;
  revenue: number | null;
  netIncome: number | null;
  cashAndDeposits: number | null;
};

/**
 * QL-39 — every field marked required gets a row the moment the widget opens
 * (no amount, no description), so the advisor sees what must be filled in and
 * the completion engine's `missingRequiredFieldIds` has a row to judge. The
 * field lists arrive already narrowed to the client's type. QL-368: the row
 * carries no `amount` at all rather than 0, so an entered 0 is a real value.
 */
function seedRequiredEntries(
  entries: FinancialEntryItem[],
  fieldDefs: FinancialFieldDefinition[],
  lang: string,
): FinancialEntryItem[] {
  const present = new Set(entries.map((e) => e.typeId));
  const seeded = fieldDefs
    .filter((f) => f.required === true && !present.has(f.id))
    .map<FinancialEntryItem>((f) => ({
      id: crypto.randomUUID(),
      typeId: f.id,
      typeLabel: resolveFieldLabel(f, lang),
      label: "",
    }));
  return seeded.length === 0 ? entries : [...entries, ...seeded];
}

export type FinancialSituationWidgetProps = {
  flowInstanceId: string;
  inputData?: string | null;
  calculatedData?: string | null;
  /** QP-195 — derived status definitions from the flow's financial situation model. */
  statusDefs?: FinancialSituationStatusDef[];
  /** QP-176/QP-183 — model business-rule settings (expecting-changes, capacity). */
  settings?: FinancialSituationModelSettings | null;
  /**
   * QP-176/QL-140 — the RAW client type. Picks the capacity variant, gates the
   * ENIN fetch (Company) and the group note (Group); defaults to Person.
   */
  clientType?: FsClientType;
  /**
   * QP-176 — registry figures already persisted for this instance (Company
   * sessions); when present the fill applies without a new ENIN call.
   */
  eninPrefill?: EninPrefill | null;
  /** QP-176 — tenant ENIN feature enabled + client resolves to an ENIN id. */
  eninAvailable?: boolean;
  eninFetching?: boolean;
  /** QP-176 — runs the ENIN lookup on demand and returns the P&L figures. */
  onFetchEninFinancials?: () => Promise<EninPrefill | null>;
  /** QP-184 — Group clients: member count scales the capacity buffer floor. */
  capacityMemberCount?: number;
  /**
   * QL-107 — the client's custom field values keyed by field id, so the live
   * preview can resolve the model's fixed-buffer trigger the moment the flag
   * changes, without waiting for a re-save of the financial situation.
   */
  clientCustomValues?: Record<string, string>;
  /** QP-183/QL-305 — the engine's completion failure reason; drives the banner + tab markers. */
  missingReason?: MissingRequirementReason;
  onChange: (input: FinancialSituationInput) => void;
  disabled?: boolean;
  incomeFields: FinancialFieldDefinition[];
  assetFields: FinancialFieldDefinition[];
  liabilityFields: FinancialFieldDefinition[];
  commitmentFields: FinancialFieldDefinition[];
  showIncome?: boolean;
  showAssets?: boolean;
  showLiabilities?: boolean;
  showCommitments?: boolean;
  /** QL-266 — hide the capacity tab (another component on the flow carries it). */
  showCapacity?: boolean;
  showStabilityQuestion?: boolean;
  /** QL-266 — hide the model's expecting-changes question on this component. */
  showExpectingChangesQuestion?: boolean;
  showCapacityClassification?: boolean;
  showSummaryMetrics?: boolean;
  currency?: string;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language?: string;
  hasValidationError?: boolean;
};

export function FinancialSituationWidget({
  flowInstanceId: _flowInstanceId,
  inputData,
  calculatedData,
  statusDefs = [],
  settings = null,
  clientType = "Person",
  eninPrefill = null,
  eninAvailable = false,
  eninFetching = false,
  onFetchEninFinancials,
  capacityMemberCount = 1,
  clientCustomValues,
  missingReason,
  onChange,
  disabled = false,
  incomeFields,
  assetFields,
  liabilityFields,
  commitmentFields,
  showIncome = true,
  showAssets = true,
  showLiabilities = true,
  showCommitments = true,
  showCapacity = true,
  showStabilityQuestion = true,
  showExpectingChangesQuestion = true,
  showCapacityClassification = true,
  showSummaryMetrics = true,
  currency = DEFAULT_CURRENCY,
  style = "none",
  title,
  description,
  language,
  hasValidationError = false,
}: FinancialSituationWidgetProps) {
  const { userLanguage } = useUserLanguage();
  const lang = language || userLanguage;
  const t = useT(T);

  // Entry items state
  const [incomeEntries, setIncomeEntries] = useState<FinancialEntryItem[]>([]);
  const [assetEntries, setAssetEntries] = useState<FinancialEntryItem[]>([]);
  const [liabilityEntries, setLiabilityEntries] = useState<FinancialEntryItem[]>([]);
  const [commitmentEntries, setCommitmentEntries] = useState<FinancialEntryItem[]>([]);

  // QP-176 — expecting-changes question (model-settings gated). QL-266 — the
  // component config can additionally hide it, so a flow that splits the
  // sections across pages asks the question once.
  const expectingChangesEnabled =
    settings?.expectingChangesQuestion?.enabled === true && showExpectingChangesQuestion;
  const [expectingChanges, setExpectingChanges] = useState<boolean | undefined>();
  const [expectingChangesNote, setExpectingChangesNote] = useState<string | undefined>();

  // QP-183 — investment capacity (model-settings gated, per client type).
  // QL-140 — shared selector; null when the variant is absent or disabled.
  const capacityConfig = capacityVariantFor(settings, clientType);
  // QL-266 — the tab is a section like the others: the config can hide it
  // here while the capacity inputs stay part of the shared saved value.
  const capacityEnabled = capacityConfig !== null && showCapacity;
  // QL-107 — same shared resolver the server calls, so the preview and the
  // stored block can never disagree about whether the policy applies.
  const fixedBuffer = resolveFixedBuffer(
    capacityConfig,
    capacityConfig?.fixedBufferClientField
      ? clientCustomValues?.[capacityConfig.fixedBufferClientField]
      : undefined,
  );
  const [monthlyIncome, setMonthlyIncome] = useState<number | undefined>();
  const [monthlyExpenses, setMonthlyExpenses] = useState<number | undefined>();
  const [monthlySurplus, setMonthlySurplus] = useState<number | undefined>();
  // QL-127 — the annual-frequency sides and the buffer mode; the engine picks
  // which sides apply from the model's variant config.
  const [annualIncome, setAnnualIncome] = useState<number | undefined>();
  const [annualExpenses, setAnnualExpenses] = useState<number | undefined>();
  const [liquidityBuffer, setLiquidityBuffer] = useState<number | undefined>();
  const [liquidityBufferMode, setLiquidityBufferMode] = useState<LiquidityBufferMode | undefined>();
  const [shortTermExpenses, setShortTermExpenses] = useState<number | undefined>();

  // Summary is only available when at least one of its sections is enabled
  const showSummary = showSummaryMetrics || showCapacityClassification;

  // QL-266 — a component that shows a single section needs no tab bar: the
  // page header (or the component title) already names it.
  const visibleTabCount = [
    showIncome,
    showAssets,
    showLiabilities,
    showCommitments,
    capacityEnabled,
    showSummary,
  ].filter(Boolean).length;

  // Active tab state — fall through visible tabs in order, never default to a hidden tab
  const [activeTab, setActiveTab] = useState<string>(
    showIncome
      ? "income"
      : showAssets
        ? "assets"
        : showLiabilities
          ? "liabilities"
          : showCommitments
            ? "commitments"
            : capacityEnabled
              ? "capacity"
              : "summary",
  );

  // Income stability
  const [incomeStability, setIncomeStability] = useState<IncomeStabilityType | undefined>();

  const [isInitialized, setIsInitialized] = useState(false);
  const onChangeRef = useRef(onChange);
  const lastSavedStateRef = useRef<string | null>(null);
  const isFirstRun = useRef(true);
  const pendingInputRef = useRef<FinancialSituationInput | null>(null);

  // Keep ref updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // QP-176/QP-183 — normalize the new optional keys to `undefined` when unset so
  // the seed / adopt / auto-save baselines all agree (JSON.stringify drops
  // undefined keys, keeping payloads byte-identical for tenants without the
  // features enabled).
  const normalizeNumber = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) ? v : undefined;
  const normalizeBoolean = (v: unknown): boolean | undefined =>
    typeof v === "boolean" ? v : undefined;
  const normalizeString = (v: unknown): string | undefined =>
    typeof v === "string" ? v : undefined;
  const normalizeBufferMode = (v: unknown): LiquidityBufferMode | undefined =>
    LIQUIDITY_BUFFER_MODES.includes(v as LiquidityBufferMode)
      ? (v as LiquidityBufferMode)
      : undefined;

  // Parse initial inputData
  useEffect(() => {
    if (inputData && !isInitialized) {
      try {
        const parsed: FinancialSituationInput = JSON.parse(inputData);
        setIncomeEntries(seedRequiredEntries(parsed.incomeEntries || [], incomeFields, lang));
        setAssetEntries(seedRequiredEntries(parsed.assetEntries || [], assetFields, lang));
        setLiabilityEntries(
          seedRequiredEntries(parsed.liabilityEntries || [], liabilityFields, lang),
        );
        setCommitmentEntries(
          seedRequiredEntries(parsed.commitmentEntries || [], commitmentFields, lang),
        );
        setIncomeStability(parsed.incomeStability);
        setExpectingChanges(normalizeBoolean(parsed.expectingChanges));
        setExpectingChangesNote(normalizeString(parsed.expectingChangesNote));
        setMonthlyIncome(normalizeNumber(parsed.monthlyIncome));
        setMonthlyExpenses(normalizeNumber(parsed.monthlyExpenses));
        setMonthlySurplus(normalizeNumber(parsed.monthlySurplus));
        setAnnualIncome(normalizeNumber(parsed.annualIncome));
        setAnnualExpenses(normalizeNumber(parsed.annualExpenses));
        setLiquidityBuffer(normalizeNumber(parsed.liquidityBuffer));
        setLiquidityBufferMode(normalizeBufferMode(parsed.liquidityBufferMode));
        setShortTermExpenses(normalizeNumber(parsed.shortTermExpenses));
        setIsInitialized(true);
      } catch {
        setIsInitialized(true);
      }
    } else if (!inputData && !isInitialized) {
      // QL-39 — nothing saved yet: still seed the required rows.
      setIncomeEntries(seedRequiredEntries([], incomeFields, lang));
      setAssetEntries(seedRequiredEntries([], assetFields, lang));
      setLiabilityEntries(seedRequiredEntries([], liabilityFields, lang));
      setCommitmentEntries(seedRequiredEntries([], commitmentFields, lang));
      setIsInitialized(true);
    }
    // The field lists and language only matter for the one-time seed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputData, isInitialized]);

  // Adopt EXTERNAL updates (e.g. an AI tool saves the financial situation) when
  // `inputData` changes after the initial seed. The init effect above only seeds once,
  // so without this a refetched value is ignored until a remount. Guard: only adopt
  // when the form has no unsaved local edits (current state still matches the last-saved
  // baseline) so the user is never clobbered mid-edit. `currency` is prop-driven, so it
  // uses the same `currency || DEFAULT_CURRENCY` shape the auto-save baseline uses.
  useEffect(() => {
    if (!isInitialized) return;

    let parsed: FinancialSituationInput | null = null;
    if (inputData) {
      try {
        parsed = JSON.parse(inputData);
      } catch {
        return;
      }
    }
    // QL-39 — seed required rows on the adopted value too, so an external
    // save without them (e.g. the public API) never strips the markers.
    const adopted = {
      incomeEntries: seedRequiredEntries(parsed?.incomeEntries || [], incomeFields, lang),
      assetEntries: seedRequiredEntries(parsed?.assetEntries || [], assetFields, lang),
      liabilityEntries: seedRequiredEntries(parsed?.liabilityEntries || [], liabilityFields, lang),
      commitmentEntries: seedRequiredEntries(
        parsed?.commitmentEntries || [],
        commitmentFields,
        lang,
      ),
    };
    const incoming = JSON.stringify(
      withoutWrongFrequencyCapacityKeys(
        {
          ...adopted,
          incomeStability: parsed?.incomeStability,
          currency: currency || DEFAULT_CURRENCY,
          expectingChanges: normalizeBoolean(parsed?.expectingChanges),
          expectingChangesNote: normalizeString(parsed?.expectingChangesNote),
          monthlyIncome: normalizeNumber(parsed?.monthlyIncome),
          monthlyExpenses: normalizeNumber(parsed?.monthlyExpenses),
          monthlySurplus: normalizeNumber(parsed?.monthlySurplus),
          annualIncome: normalizeNumber(parsed?.annualIncome),
          annualExpenses: normalizeNumber(parsed?.annualExpenses),
          liquidityBuffer: normalizeNumber(parsed?.liquidityBuffer),
          liquidityBufferMode: normalizeBufferMode(parsed?.liquidityBufferMode),
          shortTermExpenses: normalizeNumber(parsed?.shortTermExpenses),
        },
        capacityConfig,
      ),
    );
    // (a) did the server value change vs what we last applied?
    if (incoming === lastSavedStateRef.current) return;
    // (b) is the form clean (local state still equals the last-saved baseline)?
    const current = JSON.stringify(
      withoutWrongFrequencyCapacityKeys(
        {
          incomeEntries,
          assetEntries,
          liabilityEntries,
          commitmentEntries,
          incomeStability,
          currency: currency || DEFAULT_CURRENCY,
          expectingChanges,
          expectingChangesNote,
          monthlyIncome,
          monthlyExpenses,
          monthlySurplus,
          annualIncome,
          annualExpenses,
          liquidityBuffer,
          liquidityBufferMode,
          shortTermExpenses,
        },
        capacityConfig,
      ),
    );
    if (current !== lastSavedStateRef.current) return; // user mid-edit — leave it

    setIncomeEntries(adopted.incomeEntries);
    setAssetEntries(adopted.assetEntries);
    setLiabilityEntries(adopted.liabilityEntries);
    setCommitmentEntries(adopted.commitmentEntries);
    setIncomeStability(parsed?.incomeStability);
    setExpectingChanges(normalizeBoolean(parsed?.expectingChanges));
    setExpectingChangesNote(normalizeString(parsed?.expectingChangesNote));
    setMonthlyIncome(normalizeNumber(parsed?.monthlyIncome));
    setMonthlyExpenses(normalizeNumber(parsed?.monthlyExpenses));
    setMonthlySurplus(normalizeNumber(parsed?.monthlySurplus));
    setAnnualIncome(normalizeNumber(parsed?.annualIncome));
    setAnnualExpenses(normalizeNumber(parsed?.annualExpenses));
    setLiquidityBuffer(normalizeNumber(parsed?.liquidityBuffer));
    setLiquidityBufferMode(normalizeBufferMode(parsed?.liquidityBufferMode));
    setShortTermExpenses(normalizeNumber(parsed?.shortTermExpenses));
    // Advance the baseline so the auto-save effect doesn't echo this adopted update.
    lastSavedStateRef.current = incoming;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputData]);

  // QL-40 — the server-computed account snapshot (synced accounts converted to
  // the flow currency) rides in calculatedData; the widget only displays it.
  const { accountEntries, accountsSyncedAt } = useMemo<{
    accountEntries: FinancialSituationAccountEntry[];
    accountsSyncedAt: string | null;
  }>(() => {
    if (!calculatedData) return { accountEntries: [], accountsSyncedAt: null };
    try {
      const parsed = JSON.parse(calculatedData);
      return {
        accountEntries: Array.isArray(parsed?.accountEntries) ? parsed.accountEntries : [],
        accountsSyncedAt:
          typeof parsed?.accountsSyncedAt === "string" ? parsed.accountsSyncedAt : null,
      };
    } catch {
      return { accountEntries: [], accountsSyncedAt: null };
    }
  }, [calculatedData]);

  // Calculate totals from entries — QL-39: per-field sums only (the former
  // liquid / investment / secured / unsecured splits needed the field `type`).
  const calculateTotals = useCallback(() => {
    let totalAnnualIncome = 0;
    const incomeByType: Record<string, number> = {};
    for (const entry of incomeEntries) {
      totalAnnualIncome += entry.amount || 0;
      incomeByType[entry.typeId] = (incomeByType[entry.typeId] || 0) + (entry.amount || 0);
    }

    let totalAssets = 0;
    const assetsByType: Record<string, number> = {};
    for (const entry of assetEntries) {
      const amount = entry.amount || 0;
      totalAssets += amount;
      assetsByType[entry.typeId] = (assetsByType[entry.typeId] || 0) + amount;
    }

    // QL-40 — fold the server-snapshotted account values into the asset total,
    // mirroring the backend fold.
    for (const account of accountEntries) {
      if (account.amount === null) continue;
      totalAssets += account.amount;
    }

    let totalLiabilities = 0;
    let totalMonthlyDebtPayments = 0;
    const liabilitiesByType: Record<string, number> = {};
    for (const entry of liabilityEntries) {
      const amount = entry.amount || 0;
      totalLiabilities += amount;
      liabilitiesByType[entry.typeId] = (liabilitiesByType[entry.typeId] || 0) + amount;
      if (entry.monthlyPayment) {
        totalMonthlyDebtPayments += entry.monthlyPayment;
      }
    }

    let totalMonthlyCommitments = 0;
    const commitmentsByType: Record<string, number> = {};
    for (const entry of commitmentEntries) {
      const amount = entry.amount || 0;
      totalMonthlyCommitments += amount;
      commitmentsByType[entry.typeId] = (commitmentsByType[entry.typeId] || 0) + amount;
    }

    return {
      totalAnnualIncome,
      incomeByType,
      totalAssets,
      assetsByType,
      totalLiabilities,
      totalMonthlyDebtPayments,
      liabilitiesByType,
      totalMonthlyCommitments,
      commitmentsByType,
    };
  }, [incomeEntries, assetEntries, liabilityEntries, commitmentEntries, accountEntries]);

  // Calculate derived metrics — QL-39: the type-neutral ratios only.
  const calculateMetrics = useCallback((totals: ReturnType<typeof calculateTotals>) => {
    const netWorth = totals.totalAssets - totals.totalLiabilities;
    const monthlyIncome = totals.totalAnnualIncome / 12;
    const totalMonthlyExpenses = totals.totalMonthlyCommitments + totals.totalMonthlyDebtPayments;

    const debtToIncomeRatio =
      totals.totalAnnualIncome > 0 ? totals.totalLiabilities / totals.totalAnnualIncome : 0;
    const monthlyDebtServiceRatio =
      monthlyIncome > 0 ? totals.totalMonthlyDebtPayments / monthlyIncome : 0;
    const savingsRate =
      monthlyIncome > 0 ? Math.max(0, (monthlyIncome - totalMonthlyExpenses) / monthlyIncome) : 0;

    return { netWorth, debtToIncomeRatio, monthlyDebtServiceRatio, savingsRate };
  }, []);

  // QL-140 — the one assembled input: what auto-save sends AND what the
  // capacity preview is computed from, so the two can never drift.
  // The triangle keys of the frequency the variant does not read are left out:
  // the save refuses them, and a value typed before the model changed
  // frequency would otherwise block every save.
  const currentInput = useMemo<FinancialSituationInput>(
    () =>
      withoutWrongFrequencyCapacityKeys(
        {
          incomeEntries,
          assetEntries,
          liabilityEntries,
          commitmentEntries,
          incomeStability,
          currency: currency || DEFAULT_CURRENCY,
          // QP-176/QP-183 — undefined keys are dropped by JSON.stringify, keeping
          // the payload identical to pre-QP-176 when the features are unused.
          expectingChanges,
          expectingChangesNote,
          monthlyIncome,
          monthlyExpenses,
          monthlySurplus,
          annualIncome,
          annualExpenses,
          liquidityBuffer,
          liquidityBufferMode,
          shortTermExpenses,
        },
        capacityConfig,
      ),
    [
      capacityConfig,
      incomeEntries,
      assetEntries,
      liabilityEntries,
      commitmentEntries,
      incomeStability,
      currency,
      expectingChanges,
      expectingChangesNote,
      monthlyIncome,
      monthlyExpenses,
      monthlySurplus,
      annualIncome,
      annualExpenses,
      liquidityBuffer,
      liquidityBufferMode,
      shortTermExpenses,
    ],
  );

  // Auto-save on changes — send only inputData, backend calculates totals/metrics
  useEffect(() => {
    if (!isInitialized || disabled) return;

    const inputStr = JSON.stringify(currentInput);

    if (isFirstRun.current) {
      isFirstRun.current = false;
      lastSavedStateRef.current = inputStr;
      return;
    }

    if (inputStr !== lastSavedStateRef.current) {
      lastSavedStateRef.current = inputStr;
      pendingInputRef.current = currentInput;

      // Debounced — rapid edits previously fired one mutation per keystroke;
      // out-of-order responses could then overwrite the cache with stale data.
      const timer = setTimeout(() => {
        pendingInputRef.current = null;
        onChangeRef.current(currentInput);
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [isInitialized, disabled, currentInput]);

  // Flush a pending debounced save when the widget unmounts (wizard tab switch).
  useEffect(() => {
    return () => {
      if (pendingInputRef.current) {
        onChangeRef.current(pendingInputRef.current);
        pendingInputRef.current = null;
      }
    };
  }, []);

  // Get current calculated values for display
  const totals = calculateTotals();
  const metrics = calculateMetrics(totals);

  // QL-39 — required fields per category (the lists are already narrowed to
  // the client's type) and, via the shared predicate the completion engine
  // runs, which of them still lack an amount.
  const requiredIds = (fields: FinancialFieldDefinition[]) =>
    new Set(fields.filter((f) => f.required === true).map((f) => f.id));
  const requiredIncomeIds = useMemo(() => requiredIds(incomeFields), [incomeFields]);
  const requiredAssetIds = useMemo(() => requiredIds(assetFields), [assetFields]);
  const requiredLiabilityIds = useMemo(() => requiredIds(liabilityFields), [liabilityFields]);
  const requiredCommitmentIds = useMemo(() => requiredIds(commitmentFields), [commitmentFields]);
  const missingRequired = useMemo(
    () =>
      new Set(
        missingRequiredFieldIds(currentInput, [
          ...requiredIncomeIds,
          ...requiredAssetIds,
          ...requiredLiabilityIds,
          ...requiredCommitmentIds,
        ]),
      ),
    [
      currentInput,
      requiredIncomeIds,
      requiredAssetIds,
      requiredLiabilityIds,
      requiredCommitmentIds,
    ],
  );

  // QP-195 — derived statuses are computed server-side at save time and land in
  // calculatedData; the widget only reads them (no client-side derivation).
  const derivedStatuses = useMemo<Record<string, string>>(() => {
    if (!calculatedData) return {};
    try {
      const parsed = JSON.parse(calculatedData);
      return parsed?.statuses && typeof parsed.statuses === "object" ? parsed.statuses : {};
    } catch {
      return {};
    }
  }, [calculatedData]);

  // QL-702 — whether any section content renders above the expecting-changes
  // question. A summary tab kept only by the capacity classification toggle
  // shows nothing without metrics or derived statuses, so it does not count.
  // With no content the question needs no separator (and no card at all when
  // it is off too).
  const summaryHasContent = showSummaryMetrics || Object.keys(derivedStatuses).length > 0;
  const hasSectionContent =
    showIncome ||
    showAssets ||
    showLiabilities ||
    showCommitments ||
    capacityEnabled ||
    (showSummary && summaryHasContent);

  // Shared item handlers
  const handleAddItem = useCallback(
    (
      entries: FinancialEntryItem[],
      setEntries: React.Dispatch<React.SetStateAction<FinancialEntryItem[]>>,
      fieldDefs: FinancialFieldDefinition[],
    ) => {
      if (entries.length >= MAX_ITEMS_PER_CATEGORY || fieldDefs.length === 0) return;
      const firstType = fieldDefs[0];
      const newItem: FinancialEntryItem = {
        id: crypto.randomUUID(),
        typeId: firstType.id,
        typeLabel: resolveFieldLabel(firstType, lang),
        label: "",
      };
      setEntries([...entries, newItem]);
    },
    [lang],
  );

  // QL-39 — removing the last row of a required field, or re-pointing it at
  // another field, re-seeds an empty row so a required field always has one.
  const handleRemoveItem = useCallback(
    (
      id: string,
      setEntries: React.Dispatch<React.SetStateAction<FinancialEntryItem[]>>,
      fieldDefs: FinancialFieldDefinition[],
    ) => {
      setEntries((prev) =>
        seedRequiredEntries(
          prev.filter((item) => item.id !== id),
          fieldDefs,
          lang,
        ),
      );
    },
    [lang],
  );

  const handleUpdateItem = useCallback(
    (
      id: string,
      updates: Partial<FinancialEntryItem>,
      setEntries: React.Dispatch<React.SetStateAction<FinancialEntryItem[]>>,
      fieldDefs: FinancialFieldDefinition[],
    ) => {
      setEntries((prev) => {
        const next = prev.map((item) => {
          if (item.id !== id) return item;
          const updated = { ...item, ...updates };
          if (updates.typeId && updates.typeId !== item.typeId) {
            const def = fieldDefs.find((f) => f.id === updates.typeId);
            updated.typeLabel = (def ? resolveFieldLabel(def, lang) : "") || updates.typeId;
          }
          return updated;
        });
        return updates.typeId ? seedRequiredEntries(next, fieldDefs, lang) : next;
      });
    },
    [lang],
  );

  // QP-183 — live capacity preview. QL-140 — the shared engine the server runs
  // on save (incl. the QP-184 memberCount floor scaling), so the preview and
  // the persisted calculatedData can never disagree.
  const capacityPreview = useMemo(
    () =>
      capacityConfig
        ? computeCapacity(currentInput, capacityConfig, capacityMemberCount, fixedBuffer)
        : null,
    [currentInput, capacityConfig, capacityMemberCount, fixedBuffer],
  );

  // QP-183 — triangle handler: editing a field derives its counterpart so any
  // two entered values keep the third consistent (income + expenses win —
  // matches the server's authoritative derivation in computeCapacity).
  // QL-127 — a side the financial situation's entries supply is the engine's
  // figure, not the (stale) typed one, so the derivation starts from it; its
  // own input is read-only, so it is never the edited field.
  const lockedIncome =
    capacityPreview?.incomeFromEntries === true ? (capacityPreview.monthlyIncome ?? 0) : null;
  const lockedExpenses =
    capacityPreview?.expensesFromEntries === true ? (capacityPreview.monthlyExpenses ?? 0) : null;
  const handleTriangleChange = useCallback(
    (field: TriangleField, value: number | undefined) => {
      const income = lockedIncome ?? monthlyIncome;
      const expenses = lockedExpenses ?? monthlyExpenses;
      if (field === "income") {
        setMonthlyIncome(value);
        if (value !== undefined && expenses !== undefined) {
          setMonthlySurplus(value - expenses);
        } else if (value !== undefined && monthlySurplus !== undefined) {
          setMonthlyExpenses(value - monthlySurplus);
        }
      } else if (field === "expenses") {
        setMonthlyExpenses(value);
        if (income !== undefined && value !== undefined) {
          setMonthlySurplus(income - value);
        } else if (value !== undefined && monthlySurplus !== undefined) {
          setMonthlyIncome(value + monthlySurplus);
        }
      } else {
        setMonthlySurplus(value);
        if (income !== undefined && value !== undefined) {
          setMonthlyExpenses(income - value);
        } else if (expenses !== undefined && value !== undefined) {
          setMonthlyIncome(expenses + value);
        } else if (value === undefined) {
          // With one side locked the box shows the engine's derived surplus,
          // so clearing it must clear the free side too, or the engine keeps
          // deriving and the box can never read empty.
          if (lockedIncome !== null) setMonthlyExpenses(undefined);
          if (lockedExpenses !== null) setMonthlyIncome(undefined);
        }
      }
    },
    [monthlyIncome, monthlyExpenses, monthlySurplus, lockedIncome, lockedExpenses],
  );

  // QL-127 — annual frequency: only the two sides are typed; the engine
  // derives the monthly surplus.
  const handleAnnualChange = useCallback((field: AnnualField, value: number | undefined) => {
    if (field === "income") setAnnualIncome(value);
    else setAnnualExpenses(value);
  }, []);

  // QL-127 — the engine resolves the mode (a value saved before the mode
  // existed is inferred there), so the selected card and the computed block
  // read one answer. The tab only renders with a preview; "minimum" is a
  // never-shown fallback for the type.
  const bufferMode: LiquidityBufferMode = capacityPreview?.liquidityBufferMode ?? "minimum";

  // QL-140 — for groups the buffer floor is an amount PER MEMBER, so the shown
  // minimum is already multiplied. Explain it in the capacity tab, but only
  // when the floor term is the binding one: when basis × multiplier wins
  // (QL-127 — the basis is income or expenses per the variant, as the engine
  // reports it), or when a QL-107 fixed buffer replaces the derived minimum
  // outright, the floor is not what is printed above and the breakdown would
  // contradict it.
  const capacityMemberScaling = useMemo(() => {
    if (clientType !== "Group" || !capacityConfig || !capacityPreview) return null;
    if (fixedBuffer !== null) return null;
    const members = Math.max(1, capacityMemberCount);
    const floorTerm = (capacityConfig.bufferFloor || 0) * members;
    const basis =
      capacityPreview.bufferMultiplierBasis === "expenses"
        ? capacityPreview.monthlyExpenses
        : capacityPreview.monthlyIncome;
    const multiplierTerm = (basis ?? 0) * (capacityConfig.bufferMultiplier || 0);
    if (floorTerm <= 0 || floorTerm < multiplierTerm) return null;
    return { memberCount: members, floor: capacityConfig.bufferFloor || 0, total: floorTerm };
  }, [clientType, capacityConfig, capacityPreview, capacityMemberCount, fixedBuffer]);

  // QP-176 — "Fetch from ENIN": on click run the lookup (or reuse the
  // persisted one) and upsert income entries for the seeded company income
  // fields. Advisor can adjust afterwards (registry data is a source to
  // confirm, not a lock). Offered when the tenant's ENIN feature is enabled,
  // the client resolves to an ENIN id, and the model has the target fields.
  const [eninError, setEninError] = useState(false);
  const showEninButton =
    clientType === "Company" &&
    eninAvailable &&
    !!onFetchEninFinancials &&
    ENIN_FILL_TARGETS.some(({ fieldId, category }) =>
      (category === "income" ? incomeFields : assetFields).some((f) => f.id === fieldId),
    );

  const applyEninFill = useCallback(
    (prefill: EninPrefill) => {
      const entryLabel = prefill.year ? `${t.fromEnin} (${prefill.year})` : t.fromEnin;
      const upsert = (
        setEntries: React.Dispatch<React.SetStateAction<FinancialEntryItem[]>>,
        fieldDefs: FinancialFieldDefinition[],
        targets: { fieldId: string; key: keyof EninPrefill }[],
      ) => {
        setEntries((prev) => {
          const next = [...prev];
          for (const { fieldId, key } of targets) {
            const amount = prefill[key];
            const fieldDef = fieldDefs.find((f) => f.id === fieldId);
            if (amount === null || !fieldDef) continue;
            const existing = next.findIndex((e) => e.typeId === fieldId);
            if (existing >= 0) {
              next[existing] = { ...next[existing], amount, label: entryLabel };
            } else {
              next.push({
                id: crypto.randomUUID(),
                typeId: fieldId,
                typeLabel: resolveFieldLabel(fieldDef, lang),
                label: entryLabel,
                amount,
              });
            }
          }
          return next;
        });
      };
      upsert(
        setIncomeEntries,
        incomeFields,
        ENIN_FILL_TARGETS.filter((tgt) => tgt.category === "income"),
      );
      upsert(
        setAssetEntries,
        assetFields,
        ENIN_FILL_TARGETS.filter((tgt) => tgt.category === "asset"),
      );
    },
    [incomeFields, assetFields, lang, t.fromEnin],
  );

  const handleFillFromEnin = useCallback(async () => {
    if (!onFetchEninFinancials) return;
    setEninError(false);
    try {
      const prefill = await onFetchEninFinancials();
      if (prefill) {
        applyEninFill(prefill);
      } else {
        setEninError(true);
      }
    } catch {
      setEninError(true);
    }
  }, [onFetchEninFinancials, applyEninFill]);

  // QL-305 — every engine failure reason gets a specific message and the tab
  // holding the gap gets a marker, so the advisor sees WHAT is missing and
  // WHERE instead of a bare "complete your financial situation".
  // QL-354 — none of it shows before a submit attempt has flagged the
  // component (`hasValidationError`, the wizard's error-highlighting rule):
  // the banner, the tab markers, the red field borders and the deposit gate
  // (QP-183) all wait for Submit, the same as every other component. A freshly
  // opened page is not "invalid", it is unfinished.
  const missingRequiredByTab = useMemo(() => {
    const pick = (ids: Set<string>, defs: FinancialFieldDefinition[]) =>
      defs.filter((f) => ids.has(f.id) && missingRequired.has(f.id));
    return {
      income: pick(requiredIncomeIds, incomeFields),
      assets: pick(requiredAssetIds, assetFields),
      liabilities: pick(requiredLiabilityIds, liabilityFields),
      commitments: pick(requiredCommitmentIds, commitmentFields),
    };
  }, [
    missingRequired,
    requiredIncomeIds,
    requiredAssetIds,
    requiredLiabilityIds,
    requiredCommitmentIds,
    incomeFields,
    assetFields,
    liabilityFields,
    commitmentFields,
  ]);
  const showReason = missingReason !== undefined && hasValidationError;
  // Field-level red borders follow the same gate; the tabs get no ids to flag
  // until Submit has been attempted.
  const flaggedRequiredIds = hasValidationError ? missingRequired : undefined;

  let reasonMessage: string | null = null;
  if (showReason) {
    switch (missingReason) {
      case "baseline":
        reasonMessage = t.baselineMissing;
        break;
      case "required_fields_missing": {
        // Live local state, so the list shrinks as the advisor types; while the
        // debounced save has not caught up yet, fall back to the generic text.
        // QL-425 — only the tabs this component shows; the others' gaps are
        // reported on the component that carries them.
        const shownTabs = (
          [
            ["income", showIncome],
            ["assets", showAssets],
            ["liabilities", showLiabilities],
            ["commitments", showCommitments],
          ] as const
        ).flatMap(([tab, shown]) => (shown ? [tab] : []));
        const names = shownTabs.flatMap((tab) =>
          missingRequiredByTab[tab].map((f) => `${getFieldLabel(f.id, lang, [f])} (${t[tab]})`),
        );
        reasonMessage =
          names.length > 0
            ? t.requiredFieldsMissingNamed(names.join(", "))
            : t.requiredFieldsMissing;
        break;
      }
      case "assets_required":
        reasonMessage = t.assetsRequired;
        break;
      case "liabilities_required":
        reasonMessage = t.liabilitiesRequired;
        break;
      case "expecting_changes_unanswered":
        reasonMessage = t.expectingChangesUnanswered;
        break;
      case "expecting_changes_note_missing":
        reasonMessage = t.expectingChangesNoteMissing;
        break;
      case "capacity_missing":
        reasonMessage = t.capacityMissing;
        break;
      case "buffer_missing":
        reasonMessage = t.bufferMissing;
        break;
      case "buffer_below_minimum":
        reasonMessage = t.bufferBelowMinimum;
        break;
      case "deposits_exceed_capacity":
        reasonMessage = t.depositsExceedCapacity;
        break;
      case "recurring_deposits_exceed_surplus":
        reasonMessage = t.recurringDepositsExceedSurplus;
        break;
      default:
        reasonMessage = null;
    }
  }

  type FsTab = "income" | "assets" | "liabilities" | "commitments" | "capacity";
  const tabHasError = (tab: FsTab): boolean => {
    if (!showReason) return false;
    switch (missingReason) {
      case "required_fields_missing":
        return tab !== "capacity" && missingRequiredByTab[tab].length > 0;
      case "baseline":
        return tab === "income" || tab === "assets";
      case "assets_required":
        return tab === "assets";
      case "liabilities_required":
        return tab === "liabilities";
      case "capacity_missing":
      case "buffer_missing":
      case "buffer_below_minimum":
        return tab === "capacity";
      default:
        return false;
    }
  };
  const expectingChangesHasError =
    showReason &&
    (missingReason === "expecting_changes_unanswered" ||
      missingReason === "expecting_changes_note_missing");

  // Pre-bound handlers for each tab
  const incomeHandlers = {
    onAdd: () => handleAddItem(incomeEntries, setIncomeEntries, incomeFields),
    onRemove: (id: string) => handleRemoveItem(id, setIncomeEntries, incomeFields),
    onUpdate: (id: string, updates: Partial<FinancialEntryItem>) =>
      handleUpdateItem(id, updates, setIncomeEntries, incomeFields),
  };

  const assetHandlers = {
    onAdd: () => handleAddItem(assetEntries, setAssetEntries, assetFields),
    onRemove: (id: string) => handleRemoveItem(id, setAssetEntries, assetFields),
    onUpdate: (id: string, updates: Partial<FinancialEntryItem>) =>
      handleUpdateItem(id, updates, setAssetEntries, assetFields),
    onAddISINEntries: (newEntries: FinancialEntryItem[]) => {
      setAssetEntries((prev) => [...prev, ...newEntries].slice(0, MAX_ITEMS_PER_CATEGORY));
    },
  };

  const liabilityHandlers = {
    onAdd: () => handleAddItem(liabilityEntries, setLiabilityEntries, liabilityFields),
    onRemove: (id: string) => handleRemoveItem(id, setLiabilityEntries, liabilityFields),
    onUpdate: (id: string, updates: Partial<FinancialEntryItem>) =>
      handleUpdateItem(id, updates, setLiabilityEntries, liabilityFields),
  };

  const commitmentHandlers = {
    onAdd: () => handleAddItem(commitmentEntries, setCommitmentEntries, commitmentFields),
    onRemove: (id: string) => handleRemoveItem(id, setCommitmentEntries, commitmentFields),
    onUpdate: (id: string, updates: Partial<FinancialEntryItem>) =>
      handleUpdateItem(id, updates, setCommitmentEntries, commitmentFields),
  };

  // Container styling
  const getContainerClass = () => {
    if (hasValidationError) return "border border-destructive rounded-lg p-4";
    if (style === "border") return "border rounded-lg p-4";
    if (style === "box") return "bg-muted/30 rounded-lg p-4";
    return "";
  };

  return (
    <div className={`w-full space-y-4 ${getContainerClass()}`}>
      <FlowComponentHeader title={title} description={description} />
      {hasValidationError && !reasonMessage && (
        <p className="text-sm text-destructive">{t.incompleteMessage}</p>
      )}
      {reasonMessage && (
        <div className="border border-destructive/50 bg-destructive/10 text-destructive text-sm rounded-lg p-3">
          {reasonMessage}
        </div>
      )}
      {/* QL-140 — the figures describe the whole group, not one member. */}
      {clientType === "Group" && <InfoNote text={t.groupNote} />}

      <div className="w-full space-y-4">
        {/* Tab navigation — omitted when only one section is visible (QL-266) */}
        {visibleTabCount > 1 && (
          <nav className="flex gap-6 border-b">
            {showIncome && (
              <button
                type="button"
                onClick={() => setActiveTab("income")}
                className={cn(
                  "pb-2 text-sm font-medium transition-colors relative flex items-center gap-2",
                  activeTab === "income"
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.income}
                {incomeEntries.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5">
                    {incomeEntries.length}
                  </Badge>
                )}
                {tabHasError("income") && (
                  <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                )}
                {activeTab === "income" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            )}
            {showAssets && (
              <button
                type="button"
                onClick={() => setActiveTab("assets")}
                className={cn(
                  "pb-2 text-sm font-medium transition-colors relative flex items-center gap-2",
                  activeTab === "assets"
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.assets}
                {assetEntries.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5">
                    {assetEntries.length}
                  </Badge>
                )}
                {tabHasError("assets") && (
                  <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                )}
                {activeTab === "assets" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            )}
            {showLiabilities && (
              <button
                type="button"
                onClick={() => setActiveTab("liabilities")}
                className={cn(
                  "pb-2 text-sm font-medium transition-colors relative flex items-center gap-2",
                  activeTab === "liabilities"
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.liabilities}
                {liabilityEntries.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5">
                    {liabilityEntries.length}
                  </Badge>
                )}
                {tabHasError("liabilities") && (
                  <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                )}
                {activeTab === "liabilities" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            )}
            {showCommitments && (
              <button
                type="button"
                onClick={() => setActiveTab("commitments")}
                className={cn(
                  "pb-2 text-sm font-medium transition-colors relative flex items-center gap-2",
                  activeTab === "commitments"
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.commitments}
                {commitmentEntries.length > 0 && (
                  <Badge variant="secondary" className="h-5 px-1.5">
                    {commitmentEntries.length}
                  </Badge>
                )}
                {tabHasError("commitments") && (
                  <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                )}
                {activeTab === "commitments" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            )}
            {capacityEnabled && (
              <button
                type="button"
                onClick={() => setActiveTab("capacity")}
                className={cn(
                  "pb-2 text-sm font-medium transition-colors relative flex items-center gap-2",
                  activeTab === "capacity"
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.capacity}
                {tabHasError("capacity") && (
                  <TriangleAlert className="h-3.5 w-3.5 text-destructive" />
                )}
                {activeTab === "capacity" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            )}
            {showSummary && (
              <button
                type="button"
                onClick={() => setActiveTab("summary")}
                className={cn(
                  "pb-2 text-sm font-medium transition-colors relative",
                  activeTab === "summary"
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.summary}
                {activeTab === "summary" && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            )}
          </nav>
        )}

        {/* QL-107 — one bordered card around the whole tab area, so every
            tab (and the expecting-changes question below them) shares one frame. */}
        {(hasSectionContent || expectingChangesEnabled) && (
          <Card className="space-y-4 p-4">
            {/* Tab content */}
            {(activeTab === "income" || activeTab === "assets") && showEninButton && (
              <div className="flex items-center justify-end gap-3">
                {eninError && <p className="text-sm text-destructive">{t.eninFetchFailed}</p>}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleFillFromEnin}
                  disabled={disabled || eninFetching}
                >
                  {eninFetching ? t.fetchingFromEnin : t.fillFromEnin}
                  {!eninFetching && eninPrefill?.year ? ` (${eninPrefill.year})` : ""}
                </Button>
              </div>
            )}
            {showIncome && activeTab === "income" && (
              <IncomeTab
                entries={incomeEntries}
                fieldDefs={incomeFields}
                requiredTypeIds={requiredIncomeIds}
                missingRequiredIds={flaggedRequiredIds}
                disabled={disabled}
                currency={currency}
                lang={lang}
                incomeStability={incomeStability}
                showStabilityQuestion={showStabilityQuestion}
                totalAnnualIncome={totals.totalAnnualIncome}
                {...incomeHandlers}
                onStabilityChange={setIncomeStability}
              />
            )}

            {showAssets && activeTab === "assets" && (
              <AssetsTab
                entries={assetEntries}
                fieldDefs={assetFields}
                requiredTypeIds={requiredAssetIds}
                missingRequiredIds={flaggedRequiredIds}
                disabled={disabled}
                currency={currency}
                lang={lang}
                totalAssets={totals.totalAssets}
                accountRows={accountEntries}
                accountsSyncedAt={accountsSyncedAt}
                {...assetHandlers}
              />
            )}

            {showLiabilities && activeTab === "liabilities" && (
              <LiabilitiesTab
                entries={liabilityEntries}
                fieldDefs={liabilityFields}
                requiredTypeIds={requiredLiabilityIds}
                missingRequiredIds={flaggedRequiredIds}
                disabled={disabled}
                currency={currency}
                lang={lang}
                totalLiabilities={totals.totalLiabilities}
                {...liabilityHandlers}
              />
            )}

            {showCommitments && activeTab === "commitments" && (
              <CommitmentsTab
                entries={commitmentEntries}
                fieldDefs={commitmentFields}
                requiredTypeIds={requiredCommitmentIds}
                missingRequiredIds={flaggedRequiredIds}
                disabled={disabled}
                currency={currency}
                lang={lang}
                totalMonthlyCommitments={totals.totalMonthlyCommitments}
                {...commitmentHandlers}
              />
            )}

            {capacityConfig && capacityPreview && activeTab === "capacity" && (
              <CapacityTab
                disabled={disabled}
                currency={currency}
                config={capacityConfig}
                monthlyIncome={monthlyIncome}
                monthlyExpenses={monthlyExpenses}
                monthlySurplus={monthlySurplus}
                annualIncome={annualIncome}
                annualExpenses={annualExpenses}
                liquidityBuffer={liquidityBuffer}
                bufferMode={bufferMode}
                shortTermExpenses={shortTermExpenses}
                capacity={capacityPreview}
                memberScaling={capacityMemberScaling}
                fixedBuffer={fixedBuffer}
                onTriangleChange={handleTriangleChange}
                onAnnualChange={handleAnnualChange}
                onBufferChange={setLiquidityBuffer}
                onBufferModeChange={setLiquidityBufferMode}
                onShortTermChange={setShortTermExpenses}
              />
            )}

            {showSummary && summaryHasContent && activeTab === "summary" && (
              <SummaryTab
                metrics={metrics}
                currency={currency}
                lang={lang}
                showSummaryMetrics={showSummaryMetrics}
                clientType={clientType}
                derivedStatuses={derivedStatuses}
                statusDefs={statusDefs}
              />
            )}

            {/* QP-176 — expecting-changes question. Rendered below the tabs (not
            inside one) so it stays reachable regardless of section toggles;
            QL-107 keeps it inside the tab-area card. */}
            {expectingChangesEnabled && (
              <div className={cn("space-y-3", hasSectionContent && "border-t pt-4")}>
                <div className="flex items-center justify-between gap-4">
                  <Label
                    className={cn(
                      "text-sm font-normal",
                      expectingChangesHasError && "text-destructive",
                    )}
                  >
                    {t.expectingChangesQuestion}
                  </Label>
                  <div className="flex items-center gap-1 rounded-lg border p-1 shrink-0">
                    <Button
                      type="button"
                      variant={expectingChanges === true ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setExpectingChanges(true)}
                      disabled={disabled}
                      className="h-7 px-3 text-xs"
                    >
                      {t.yes}
                    </Button>
                    <Button
                      type="button"
                      variant={expectingChanges === false ? "secondary" : "ghost"}
                      size="sm"
                      onClick={() => setExpectingChanges(false)}
                      disabled={disabled}
                      className="h-7 px-3 text-xs"
                    >
                      {t.no}
                    </Button>
                  </div>
                </div>
                {expectingChanges === true && (
                  <div className="space-y-2">
                    <Label className="text-sm">
                      {t.expectingChangesNote} <span className="text-destructive">*</span>
                    </Label>
                    <Textarea
                      value={expectingChangesNote ?? ""}
                      onChange={(e) => setExpectingChangesNote(e.target.value)}
                      disabled={disabled}
                      rows={3}
                    />
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
}
